/**
 * Avatar Initialization Configuration
 */
export const AVATAR_INIT_TIMEOUT = 30000; // 30 seconds

export const AVATAR_CHROMA_KEY_OPTIONS = {
  minHue: 60, // minimum hue value (0-360)
  maxHue: 180, // maximum hue value (0-360)
  minSaturation: 0.1, // minimum saturation (0-1)
  threshold: 1.3, // threshold for green detection
};

export const AVATAR_NAME = "default";
export const AVATAR_QUALITY = "medium";

/**
 * Azure Custom Avatar Configuration
 */
export const AZURE_AVATAR_CONFIG = {
  // Default settings
  DEFAULT_VOICE_NAME: "en-US-DavisNeural",
  DEFAULT_AVATAR_CHARACTER: "Wisam-1",
  DEFAULT_SERVICE_REGION: "westeurope",
  DEFAULT_AVATAR_STYLE: "",

  // Timing configuration
  CONTAINER_CHECK_INTERVAL_MS: 100, // Reduced from 500ms for faster detection
  MAX_CONTAINER_WAIT_ATTEMPTS: 50, // Increased to maintain same total timeout
  CLEANUP_DELAY_MS: 300, // Reduced from 500ms for faster reconnection
  RECOVERY_DELAY_MS: 2000, // Reduced from 3000ms for faster recovery
  FAILED_CONNECTION_RECOVERY_DELAY_MS: 500, // Reduced from 1000ms
  REINITIALIZATION_DELAY_MS: 500, // Reduced from 1000ms

  // Video configuration
  VIDEO_CROP_WIDTH: 1920,
  VIDEO_CROP_HEIGHT: 1080,
  VIDEO_CROP_X: 0,
  VIDEO_CROP_Y: 0,

  // Background configuration
  BACKGROUND_IMAGE_URL:
    process.env.REACT_APP_AVATAR_BACKGROUND_IMAGE_URL || "",
  FALLBACK_BACKGROUND_COLOR:
    process.env.REACT_APP_AVATAR_FALLBACK_BACKGROUND_COLOR || "#00000000",

  // Audio configuration
  AUDIO_VOLUME: Number(process.env.REACT_APP_AVATAR_AUDIO_VOLUME) || 1,
  SSML_VOLUME: process.env.REACT_APP_AVATAR_SSML_VOLUME || "+20%", // Slightly louder for clarity
  SSML_RATE: process.env.REACT_APP_AVATAR_SSML_RATE || "0.95", // 5% slower than normal for more natural, less robotic speech
  SSML_PITCH: process.env.REACT_APP_AVATAR_SSML_PITCH || "+5%", // Subtle pitch variation for warmth
} as const;

/**
 * Azure Avatar Element IDs
 */
export const AVATAR_ELEMENT_IDS = {
  AVATAR_VIDEO: "avatar-video",
  AVATAR_CANVAS: "avatarCanvas",
  AZURE_AVATAR_VIDEO: "azure-avatar-video",
  AVATAR_CONTAINER: "avatar-container",
} as const;

/**
 * Azure Avatar CSS Classes
 */
export const AVATAR_CSS_CLASSES = {
  AVATAR_VIDEO: "avatar-video",
  STATUS_OVERLAY: "status-overlay",
} as const;

/**
 * Azure Avatar Status Types
 */
export const AVATAR_STATUS_TYPES = {
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
} as const;

/**
 * Azure Avatar Status Configuration
 */
export const AVATAR_STATUS_CONFIG = {
  ICONS: {
    LOADING: "⏳",
    READY: "✓",
    ERROR: "✗",
  },
  COLORS: {
    LOADING: "#007bff",
    READY: "#28a745",
    ERROR: "#dc3545",
  },
  OVERLAY_Z_INDEX: 1000,
} as const;

/**
 * SSML Configuration
 */
export const SSML_CONFIG = {
  VERSION: "1.0",
  XMLNS: "http://www.w3.org/2001/10/synthesis",
  XMLNS_MSTTS: "http://www.w3.org/2001/mstts",
  LANG: "en-US",
} as const;

/**
 * WebRTC Configuration
 */
export const WEBRTC_CONFIG = {
  ICE_TRANSPORT_POLICY: "all",
  TRANSCEIVER_DIRECTION: "sendrecv",
  // Performance optimizations
  ICE_CANDIDATE_POOL_SIZE: 10, // Pre-gather ICE candidates for faster connection
  BUNDLE_POLICY: "max-bundle", // Use single transport for better performance
  RTCP_MUX_POLICY: "require", // Reduce port usage and improve NAT traversal
} as const;
